import { Emotion } from '@emotion/css/types/create-instance';
import { CSSInterpolation, InterpolationPrimitive, SerializedStyles } from '@emotion/serialize';

const isSerializedStyles = (primitive: InterpolationPrimitive): primitive is SerializedStyles =>
  Boolean(
    typeof primitive === 'object' &&
      primitive &&
      'styles' in primitive &&
      typeof primitive.styles === 'string',
  );

const fixInterpolationPrimitive = (primitive: InterpolationPrimitive) => {
  // https://github.com/emotion-js/emotion/issues/2826
  if (isSerializedStyles(primitive) && primitive.styles && !primitive.styles.endsWith(';')) {
    // eslint-disable-next-line no-param-reassign -- It's safe to mutate this because styles are only serialized when they are passed directly to `css`.
    primitive.styles += ';';
  }
};

const fixInterpolation = (interpolation: TemplateStringsArray | CSSInterpolation): void => {
  if (Array.isArray(interpolation)) {
    interpolation.forEach(fixInterpolation);
  } else {
    // `isArray` doesn't narrow away `TemplateStringsArray`. This cast
    // intentionally avoids narrowing directly to `InterpolationPrimitive` so as
    // to be conservative with respect to future changes.
    fixInterpolationPrimitive(interpolation as Exclude<typeof interpolation, TemplateStringsArray>);
  }
};

type CssFunction = Emotion['css'];

export const fixCss =
  (css: CssFunction): CssFunction =>
  (...args) => {
    // These are split up only to simplify typing the parameter of `fixInterpolation`.
    const [first, ...rest] = args;
    fixInterpolation(first);
    fixInterpolation(rest);

    // A cast is required due to `css` being overloaded.
    return css(...(args as Parameters<CssFunction>));
  };
